import { useState, useRef, useEffect } from 'react';
import { ThumbsDown, ThumbsUp, CirclePlay, CircleStop, LoaderCircle, RefreshCcw } from 'lucide-react';
import { getResponseAudio, sendFeedback } from '../services/feedback-service';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useTheme } from '../../../components/core/theme-provider';
import { useConfig } from 'hooks/use-config';
import { useToast } from '../../../hooks/use-toast';
import { MessageGeneration } from '../types/chat';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, FeatureName } from 'analytics/events';
import { PipelineAnalytics } from './PipelineAnalytics';

type FeedbackProps = {
  message: MessageGeneration;
  recordId: string;
  isStillGenerating: boolean;
  hideRegenerateButton: boolean;
  hasParticipants?: boolean;
};

export function Feedback({ message, recordId, isStillGenerating, hideRegenerateButton, hasParticipants }: FeedbackProps) {

  const conversationId = useChatStore((state) => state.activeConversationId);
  const activeAudioMessageId = useChatStore((state) => state.activeAudioMessageId);
  const setActiveAudioMessageId = useChatStore((state) => state.setActiveAudioMessageId);
  const updateMessageFeedback = useChatStore((state) => state.updateMessageFeedback);
  const regenerateResponse = useChatStore((state) => state.regenerateResponse);
  const [liked, setLiked] = useState<boolean | undefined>(message.feedback);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const [isAudioLoading, setIsAudioLoading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const mediaSourceRef = useRef<MediaSource>(new MediaSource());
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const cachedAudioBlobRef = useRef<Blob | null>(null);
  const handleSourceOpenRef = useRef<(() => void) | null>(null);
  const canPlayRef = useRef<(() => void) | null>(null);
  const endedRef = useRef<(() => void) | null>(null);
  const errorRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setLiked(message.feedback);

    cleanupAudio();
    if (cachedAudioBlobRef.current) cachedAudioBlobRef.current = null;
    mediaSourceRef.current = new MediaSource();
    audioRef.current = new Audio();
  }, [message.id]);


  const cleanupAudio = () => {
    if (audioRef.current) {
      const audio = audioRef.current;
      if (canPlayRef.current) audio.removeEventListener('canplay', canPlayRef.current);
      if (endedRef.current) audio.removeEventListener('ended', endedRef.current);
      if (errorRef.current) audio.removeEventListener('error', errorRef.current);

      if (audio.src) {
        URL.revokeObjectURL(audio.src);
      }
      audio.src = '';
      audio.pause();
      audio.load();
    }

    if (mediaSourceRef.current && mediaSourceRef.current.readyState === 'open') {
      try {
        mediaSourceRef.current.endOfStream();
      } catch (e) {
        console.warn('Failed to end media source stream:', e);
      }
    }
    mediaSourceRef.current.removeEventListener('sourceopen', handleSourceOpenRef.current!);
    setIsPlaying(false);
    setIsAudioLoading(false);
  };

  const handleThumbsClick = async (messageId: string, isLiked: boolean) => {
    if (!conversationId) return;
    if (isLiked === liked) return;

    const prevLiked = liked;

    try {
      setLiked(isLiked);
      await sendFeedback(
        {
          conversation_id: conversationId,
          msg_id: messageId,
          feedback: isLiked,
        },
        configurationSettings
      );
      updateMessageFeedback(recordId, messageId, isLiked);
    } catch (error) {
      setLiked(prevLiked);
      console.error('Failed to send feedback:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_SEND_FEEDBACK'),
        description: t('FAILED_TO_SEND_FEEDBACK_DESCRIPTION'),
      });
    }
  };

  const handleRegenerateClick = () => {
    // console.log('recordId in handleRegenerateClick', recordId)
    // if (!conversationId) return;
    // const currentConversation = useChatStore.getState().conversations[conversationId];
    // currentConversation
    if (!recordId) return;
    regenerateResponse(recordId, configurationSettings);
  };

  const handlePlayClick = async (messageId: string) => {
    // If there's another audio playing, stop it first
    if (activeAudioMessageId && activeAudioMessageId !== messageId) {
      setActiveAudioMessageId(null);
    }

    // Set this message as the active audio
    setActiveAudioMessageId(messageId);

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setActiveAudioMessageId(null);
      return;
    }

    if (cachedAudioBlobRef.current) {
      const audio = new Audio(URL.createObjectURL(cachedAudioBlobRef.current));
      audioRef.current = audio;
      audio.play();
      setIsPlaying(true);

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setActiveAudioMessageId(null);
      });
      return;
    }

    trackEvent(EVENTS.FEATURE_USED(FeatureName.READ_ALOUD));

    try {
      setIsAudioLoading(true);
      const response = getResponseAudio({
        conversation_id: conversationId!,
        msg_id: messageId,
      }, configurationSettings);

      console.log("At handlePlayClick ", response, new Date().toISOString());

      audioRef.current.src = URL.createObjectURL(mediaSourceRef.current);

      canPlayRef.current = () => {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
          setIsAudioLoading(false);
          setActiveAudioMessageId(null);
        });
        setIsPlaying(true);
        setIsAudioLoading(false);
      };

      endedRef.current = () => {
        setIsPlaying(false);
        setActiveAudioMessageId(null);
      };

      errorRef.current = () => {
        setIsPlaying(false);
        setIsAudioLoading(false);
        setActiveAudioMessageId(null);
      };

      audioRef.current.addEventListener('canplay', canPlayRef.current);
      audioRef.current.addEventListener('ended', endedRef.current);
      audioRef.current.addEventListener('error', errorRef.current);

      const handleSourceOpen = async () => {
        if (!mediaSourceRef.current) return;
        console.log("At handleSourceOpen ", audioRef.current, new Date().toISOString());

        try {
          const mime = 'audio/mpeg';
          const sourceBuffer = mediaSourceRef.current.addSourceBuffer(mime);

          if (!response) {
            console.warn('Response not available');
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            return;
          }

          let _response;
          try {
            _response = await response;
          } catch (error) {
            console.log('Streaming error:', error);
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            toast({
              title: t('STREAMING_ERROR'),
              description: t('FAILED_TO_STREAM_AUDIO'),
              variant: 'destructive',
            });
            return;
          }

          if (!sourceBuffer) {
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            console.warn('Source buffer not available');
            return;
          }

          if (!_response.body) {
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            console.warn('Response body not available');
            return;
          }

          const reader = _response.body.getReader();
          const chunks: Uint8Array[] = [];

          const pump = async () => {
            if (!mediaSourceRef.current || !sourceBuffer) {
              return;
            }

            const { done, value } = await reader.read();

            if (done) {
              if (mediaSourceRef.current?.readyState === 'open') {
                mediaSourceRef.current.endOfStream();
              }
              const blob = new Blob(chunks, { type: 'audio/mpeg' });
              cachedAudioBlobRef.current = blob;
              return;
            }

            chunks.push(value!);

            if (!sourceBuffer) {
              return;
            }

            if (!sourceBuffer.updating) {
              if (mediaSourceRef.current?.readyState === 'open') {
                sourceBuffer.appendBuffer(value!);
              } else {
                return;
              }
            } else {
              const onUpdate = () => {
                if (!sourceBuffer) {
                  return;
                }
                sourceBuffer.removeEventListener('updateend', onUpdate);
                sourceBuffer.appendBuffer(value!);
              };
              sourceBuffer.addEventListener('updateend', onUpdate);
            }

            if (sourceBuffer) {
              sourceBuffer.addEventListener('updateend', pump, { once: true });
            }
          };

          pump();
        } catch (error) {
          console.error('Streaming error:', error);
          if (mediaSourceRef.current?.readyState === 'open') {
            try {
              mediaSourceRef.current.endOfStream();
            } catch (e) {
              console.warn('Failed to end media source stream:', e);
            }
          }
          setIsAudioLoading(false);
          setActiveAudioMessageId(null);
        }
      };

      handleSourceOpenRef.current = handleSourceOpen;
      mediaSourceRef.current.addEventListener('sourceopen', handleSourceOpen);
    } catch (error) {
      console.log('Streaming error:', error);
      setIsAudioLoading(false);
      setActiveAudioMessageId(null);
      toast({
        title: t('STREAMING_ERROR'),
        description: t('FAILED_TO_STREAM_AUDIO'),
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    return () => {
      cleanupAudio();
    }
  }, []);

  useEffect(() => {
    if (activeAudioMessageId && activeAudioMessageId !== message.id) {
      cleanupAudio();
    }
  }, [activeAudioMessageId, message.id]);

  return (
    <div className="flex md:gap-2 lg:gap-2 justify-start">
      <button
        title={t('GOOD_RESPONSE')}
        className="flex items-center justify-center h-8 w-8  pl-0 pr-1.5 py-1.5 rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handleThumbsClick(message.id, true)}
      // disabled={hasParticipants}
      >
        <ThumbsUp
          size={16}
          className={
            liked !== undefined && liked
              ? `${theme === 'light' ? 'fill-black' : 'fill-gray-100'}`
              : 'fill-none'
          }
        />
      </button>

      <button
        title={t('BAD_RESPONSE')}
        className="flex items-center justify-center h-8 w-8 pl-0 pr-1.5 py-1.5 rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handleThumbsClick(message.id, false)}

      >
        <ThumbsDown
          size={16}
          className={
            liked !== undefined && !liked
              ? `${theme === 'light' ? 'fill-black' : 'fill-gray-100'}`
              : 'fill-none'
          }
        />
      </button>

      {!message.HasFileGeneratedResponse && <button
        title={t('READ_ALOUD')}
        className="flex items-center justify-center h-8 w-8 pl-0 pr-1.5 py-1.5 rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handlePlayClick(message.id)}
      >
        {isAudioLoading ? <LoaderCircle className='animate-spin' size={16} /> : isPlaying ? <CircleStop size={16} /> : <CirclePlay size={16} />}
      </button>}

      {(!isStillGenerating && !hideRegenerateButton && !hasParticipants) && <button
        title={'Regenerate'}
        className="flex items-center justify-center h-8 w-8 pl-0 pr-1.5 py-1.5 rounded-full hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleRegenerateClick}

      >
        <RefreshCcw size={16} />
      </button>}

      {conversationId && <PipelineAnalytics message={message} conversationId={conversationId} recordId={recordId} />}
    </div>
  );
}
